import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem } from "../ui/carousel";

const SLIDES = [
  {
    key: "refer",
    tag: "Your reward",
    title: "Refer a friend, earn together",
    desc: "Know someone who should go solar? Share your link — you both win when they switch to Purvodaya.",
    bullets: ["Instant referral tracking", "Payout the moment they install"],
    gradient: "from-amber-500 via-orange-500 to-rose-500",
    icon: Gift,
    ctaLabel: "Refer now",
    to: "/app/profile",
  },
  {
    key: "residential",
    tag: "Cut bills 70–95%",
    title: "Residential Rooftop Solar",
    desc: "Power every fan, AC, fridge and pump straight from the sun — claim up to ₹78,000 central subsidy.",
    bullets: ["₹78,000 PM Surya Ghar subsidy", "25-year panel warranty"],
    image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1600&q=80&auto=format&fit=crop",
    gradient: "from-emerald-600 via-emerald-700 to-[#0D4721]",
    ctaLabel: "Get a quote",
    to: "/app/buy-installation?category=residential",
  },
  {
    key: "commercial",
    tag: "Up to 40% tax saving",
    title: "Commercial & Industrial Solar",
    desc: "MW-scale rooftop & ground-mount systems for factories, offices and warehouses.",
    bullets: ["40% accelerated depreciation", "60%+ bill reduction"],
    image: "https://images.unsplash.com/photo-1504762300654-109d62172feb?w=1600&q=80&auto=format&fit=crop",
    gradient: "from-sky-600 via-sky-700 to-indigo-800",
    ctaLabel: "Get a quote",
    to: "/app/buy-installation?category=commercial",
  },
  {
    key: "ev",
    tag: "AC 7kW · DC 60kW+",
    title: "EV Charging Solutions",
    desc: "Solar-powered AC & DC fast chargers for homes, offices and highway plazas.",
    bullets: ["Solar + grid hybrid", "True zero-cost driving"],
    image: "https://images.unsplash.com/photo-1744973270493-01a1f5e5e1c4?w=1600&q=80&auto=format&fit=crop",
    gradient: "from-violet-600 via-purple-700 to-fuchsia-800",
    ctaLabel: "Enquire now",
    to: "/app/buy-installation?category=ev_charger",
  },
  {
    key: "bess",
    tag: "24×7 clean power",
    title: "Battery Energy Storage",
    desc: "Store surplus solar by day, power your home through outages and evenings.",
    bullets: ["5 kWh home → 1 MWh+ industrial", "10-year warranty"],
    image: "https://images.unsplash.com/photo-1589276534126-adef63a95e05?w=1600&q=80&auto=format&fit=crop",
    gradient: "from-teal-600 via-cyan-700 to-slate-800",
    ctaLabel: "Enquire now",
    to: "/app/buy-installation?category=battery_storage",
  },
  {
    key: "street_light",
    tag: "Zero wiring · zero bill",
    title: "Solar Street Lighting",
    desc: "Dusk-to-dawn lighting for campuses, societies and rural roads — no grid, no trenching.",
    bullets: ["Auto dusk-to-dawn + motion sensing", "5-year warranty"],
    image: "https://images.unsplash.com/photo-1740805276608-ef60e2e468ba?w=1600&q=80&auto=format&fit=crop",
    gradient: "from-indigo-700 via-blue-900 to-slate-900",
    ctaLabel: "Enquire now",
    to: "/app/buy-installation?category=street_light",
  },
  {
    key: "housing_society",
    tag: "RWA-friendly",
    title: "Housing Society Solar",
    desc: "Group rooftop solar for apartment complexes — society savings plus per-flat subsidy.",
    bullets: ["₹18,000/kW society subsidy", "Lifts, pumps & lobby on solar"],
    image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1600&q=80&auto=format&fit=crop",
    gradient: "from-rose-600 via-pink-700 to-fuchsia-900",
    ctaLabel: "Get a quote",
    to: "/app/buy-installation?category=housing_society",
  },
  {
    key: "water_heater",
    tag: "100–500 LPD",
    title: "Solar Water Heaters",
    desc: "Evacuated-tube solar water heaters — hot water 365 days, zero geyser bills.",
    bullets: ["Hot water 365 days", "Cuts geyser bills entirely"],
    image: "https://images.unsplash.com/photo-1770178995777-31f3af9e6aa2?w=1600&q=80&auto=format&fit=crop",
    gradient: "from-orange-600 via-red-600 to-rose-800",
    ctaLabel: "Enquire now",
    to: "/app/buy-installation?category=others",
  },
];

export default function CustomerPromoBanner() {
  const navigate = useNavigate();
  const [api, setApi] = useState(null);
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setActive(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => api.off("select", onSelect);
  }, [api]);

  useEffect(() => {
    if (!api) return;
    timerRef.current = setInterval(() => api.scrollNext(), 4500);
    return () => clearInterval(timerRef.current);
  }, [api]);

  return (
    <div data-testid="customer-promo-banner" className="-mx-5 px-5 mb-2">
      <Carousel setApi={setApi} opts={{ loop: true }} className="w-full">
        <CarouselContent>
          {SLIDES.map((s) => {
            const Icon = s.icon;
            return (
              <CarouselItem key={s.key}>
                <div
                  data-testid={`promo-slide-${s.key}`}
                  className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${s.gradient} h-[172px] p-4 flex flex-col justify-between`}
                >
                  {s.image && (
                    <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-overlay" draggable={false} />
                  )}
                  <div className="relative z-10">
                    <div className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-white/80 bg-black/20 px-2 py-0.5 rounded-full">
                      {Icon && <Icon className="w-3 h-3" />} {s.tag}
                    </div>
                    <div className="font-brand text-[21px] font-semibold text-white mt-1.5 leading-snug">{s.title}</div>
                    <p className="text-[11.5px] text-white/85 mt-1 leading-snug max-w-[82%]">{s.desc}</p>
                  </div>
                  <div className="relative z-10 flex items-end justify-between gap-2">
                    <button
                      onClick={() => navigate(s.to)}
                      data-testid={`promo-slide-cta-${s.key}`}
                      className="inline-flex items-center gap-1 bg-white text-[#0F172A] text-[11px] font-bold px-3 py-1.5 rounded-full hover:scale-105 active:scale-95 transition-transform"
                    >
                      {s.ctaLabel} <ArrowRight className="w-3 h-3" />
                    </button>
                    <a
                      href="https://purvodayasolar.com"
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`promo-slide-site-link-${s.key}`}
                      className="text-[9.5px] text-white/75 hover:text-white underline-offset-2 hover:underline"
                    >
                      Visit purvodayasolar.com for more details
                    </a>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
      <div className="flex items-center justify-center gap-1.5 mt-2.5" data-testid="promo-banner-dots">
        {SLIDES.map((s, i) => (
          <button
            key={s.key}
            onClick={() => api?.scrollTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={"h-1.5 rounded-full transition-all " + (active === i ? "w-5 bg-[#0D4721]" : "w-1.5 bg-slate-300")}
          />
        ))}
      </div>
    </div>
  );
}
